import React, { useState } from "react";
import dayjs from "dayjs";

import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ErrorIcon from "@mui/icons-material/Error";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";

import { GridActionsCellItem } from "@mui/x-data-grid";

import FeedbackSubmissionDialog from "./feedbackSubmissionDialog";
import ResponsiveGrid from "../grid/responsiveGrid";

import useGetRequest from "../hooks/useGetRequest";

import { CustomPagination } from "../customPagination";
import { getHexColorStringFromFeedbackStatus } from "./feedbackUtil";

const FeedbackSubmissionsDatagrid = () => {
    const [selectedFeedbackSubmissionUuid, setSelectedFeedbackSubmissionUuid] = useState(null);

    const { data, setData, loading, error } = useGetRequest(`/api/feedback/get`);

    const columns = [
        {
            field: "feedback_type",
            headerName: "Type",
            minWidth: 125,
            editable: false
        },
        {
            field: "user_email",
            headerName: "User",
            minWidth: 200,
            editable: false
        },
        {
            field: "user_organization_name",
            headerName: "Organization",
            minWidth: 125,
            editable: false
        },
        {
            field: "feedback_description",
            headerName: "Description",
            minWidth: 175,
            flex: 1,
            editable: false
        },
        {
            field: "status",
            headerName: "Status",
            width: 125,
            type: "singleSelect",
            editable: true,
            valueOptions: () => ["NEW", "IN_REVIEW", "CLOSED"],
            renderCell: ({ row }) => {
                return (
                    <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", height: "100%" }}>
                        <Typography variant="body1" color={getHexColorStringFromFeedbackStatus(row.status)}>
                            {row.status}
                        </Typography>
                    </Box>
                );
            }
        },
        {
            field: "date_created",
            headerName: "Created",
            minWidth: 225,
            editable: false,
            valueGetter: (value) => `${dayjs(value).format("MMMM D, YYYY [at] h:mm a")}`
        },
        {
            field: "can_contact",
            headerName: "Can Contact",
            minWidth: 125,
            editable: false,
            headerAlign: "center",
            renderCell: ({ row }) => (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <Checkbox checked={row.can_contact} disabled={true} />
                </Box>
            )
        },
        {
            type: "actions",
            field: "actions",
            minWidth: 125,
            headerName: "Actions",
            cellClassName: "actions",
            getActions: ({ row }) => {
                const options = [];

                options.push(
                    <Tooltip key={1} title="Expand">
                        <GridActionsCellItem icon={<OpenInNewIcon />} label="Expand" onClick={() => setSelectedFeedbackSubmissionUuid(row.uuid)} />
                    </Tooltip>
                );
                options.push(
                    <Tooltip key={2} title="Delete">
                        <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={() => handleDeleteButtonClick(row)} />
                    </Tooltip>
                );
                return options;
            }
        }
    ];
    const handleDeleteButtonClick = async (submission) => {
        try {
            if (!confirm("Are you sure you want to delete this feedback submission?")) {
                return;
            }
            const requestOptions = {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            };
            const response = await fetch(`api/feedback/delete/${submission.uuid}`, requestOptions);
            if (!response.ok) throw new Error(response);

            const updatedData = await response.json();
            setData(updatedData);
        } catch (err) {
            console.error(err);
        }
    };
    const handleFeedbackSubmissionChange = async (updated) => {
        try {
            const requestOptions = {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(updated)
            };
            const response = await fetch(`api/feedback/update`, requestOptions);
            if (!response.ok) throw new Error(response);

            const updatedData = await response.json();
            setData(updatedData);

            return updated;
        } catch (err) {
            console.error(err);
        }
    };
    return (
        <Box sx={{ p: 3, height: "100%", width: "100%", display: "flex", flexDirection: "column" }}>
            <Typography component="h2" variant="h6" color="#6b778c" noWrap sx={{ flexShrink: 0 }}>
                Feedback Submissions
            </Typography>

            <Paper sx={{ p: 2, mt: 2, display: "flex", flexDirection: "column", gap: 2, flex: 1, overflowY: "auto" }}>
                {!error ? (
                    <ResponsiveGrid
                        rows={data}
                        columns={columns}
                        disableRowSelectionOnClick
                        slots={{ pagination: CustomPagination }}
                        getRowId={(row) => row.uuid}
                        processRowUpdate={(updated) => handleFeedbackSubmissionChange(updated)}
                        onProcessRowUpdateError={(err) => console.error(err)}
                        loading={loading}
                    />
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", gap: 2 }}>
                        <ErrorIcon fontSize="large" />

                        <Box sx={{ maxWidth: "50%" }}>
                            <Typography gutterBottom color="textPrimary" variant="h6">
                                Unable to load feedback submissions
                            </Typography>

                            <Typography color="textSecondary" variant="body1">
                                Please refresh the page or try again in a few moments.
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Paper>

            {selectedFeedbackSubmissionUuid ? (
                <FeedbackSubmissionDialog
                    feedbackSubmissions={data}
                    setFeedbackSubmissions={setData}
                    selectedFeedbackSubmissionUuid={selectedFeedbackSubmissionUuid}
                    setSelectedFeedbackSubmissionUuid={setSelectedFeedbackSubmissionUuid}
                />
            ) : (
                <></>
            )}
        </Box>
    );
};

export default FeedbackSubmissionsDatagrid;
